import { useLazyQuery } from '@apollo/client/react';
import { useAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { ScrollBox } from '~/components/blocks';
import { useExistsAppointment } from '~/components/partials/PatientKarte/use-exists-appointment';
import { TracingReportPanel } from '~/components/partials/TracingReportPanel';
import {
  GetPatientTracingReportsDocument,
  GetPatientTracingReportsQuery,
  TracingReportFragment,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { usePreviousValue } from '~/hooks/use-previous-value';
import { patientDetailDialogState } from '~/state/partials/patient_detail_dialog/atoms';
import { TabType } from '~/state/partials/patient_detail_dialog/types';
import { tracingReportPageInfoState } from '~/state/partials/tracingreport_panel/atoms';

type Props = {
  patientId: string;
  online?: boolean;
};

const getTotalPage = (data: GetPatientTracingReportsQuery) => {
  const tracingReports = getNode(data, 'Patient')?.tracingReports;
  return tracingReports ? tracingReports.pagesCount : 1;
};

export const TracingReport = (props: Props) => {
  const { patientId, online } = props;
  const [pageInfo, setPageInfo] = useAtom(tracingReportPageInfoState);
  const [patientDetailDialog, setPatientDetailDialogState] = useAtom(patientDetailDialogState);
  const isOpen = patientDetailDialog.isOpen;

  const scrollRef = useRef<RefAttributeType<typeof ScrollBox> | null>(null);
  const { exists, loading: loadingCount } = useExistsAppointment(patientId);
  const creatable = !online && exists && !loadingCount;

  const [getTracingReports, { data, loading }] = useLazyQuery(GetPatientTracingReportsDocument, {
    fetchPolicy: 'network-only',
  });
  const prevData = usePreviousValue(data);
  const currentData = data || prevData;
  const tracingReports: TracingReportFragment[] = useMemo(() => {
    let results: TracingReportFragment[] = [];
    const patient = getNode(currentData, 'Patient');
    if (patient) {
      results = patient.tracingReports.nodes.map((n) => {
        return { id: n.id, createdAt: n.createdAt, data: n.data };
      });
    }

    return results;
  }, [currentData]);

  const handleCreateTracingReport = useCallback(() => {
    if (patientId) {
      setPatientDetailDialogState((_state) => ({
        ..._state,
        patientId,
        isOpen: true,
        type: 'tracingreport' as TabType,
      }));
    }
  }, [patientId, setPatientDetailDialogState]);

  useEffect(() => {
    if (pageInfo.target == 'Patient' && patientId && !isOpen) {
      getTracingReports({
        variables: {
          patientId: patientId,
          page: pageInfo.page,
          perPage: pageInfo.perPage,
        },
      });
    }
  }, [
    isOpen,
    getTracingReports,
    patientId,
    pageInfo.page,
    pageInfo.perPage,
    pageInfo.target,
    patientDetailDialog.isOpen,
  ]);

  useEffect(() => {
    if (!data) return;

    setPageInfo((_state) => ({ ..._state, totalPage: getTotalPage(data) }));
    scrollRef.current?.toTop();
  }, [data, setPageInfo]);

  return (
    <TracingReportPanel
      onCreate={handleCreateTracingReport}
      loading={loading}
      tracingReports={tracingReports}
      creatable={creatable}
    />
  );
};
